/**
 * Marketing → app language handoff (cross-origin safe via query param).
 *
 * Marketing appends `?lang=en|de|tr`. This module reads it (overriding
 * saved preference when present) and strips it after consume — same idea
 * as auth `?vt=`. Prefer clearing via React Router `navigate(..., { replace })`
 * so in-memory location stays in sync (see LanguageContext).
 */

import { Lang } from "./types";

const LANG_PARAM = "lang";
const ALLOWED_LANGS = new Set<Lang>(["en", "de", "tr"]);

export function isAllowedLang(value: string): value is Lang {
  return ALLOWED_LANGS.has(value as Lang);
}

function searchParamsFrom(search?: string): URLSearchParams {
  if (search != null) {
    return new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search,
    );
  }
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

/** Valid `?lang=` only; empty string if missing or unsupported. */
export function readLangFromUrl(search?: string): Lang | "" {
  const raw = searchParamsFrom(search).get(LANG_PARAM);
  if (!raw) return "";
  const lang = raw.toLowerCase();
  return isAllowedLang(lang) ? lang : "";
}

/** True when the URL has a `lang` query key (valid or not). */
export function hasLangQueryParam(search?: string): boolean {
  return searchParamsFrom(search).has(LANG_PARAM);
}

/**
 * Build the next search string with `lang` removed (leading `?` or empty).
 * Use with React Router navigate for a synced replace.
 */
export function searchWithoutLangParam(search: string): string {
  const params = searchParamsFrom(search);
  params.delete(LANG_PARAM);
  const q = params.toString();
  return q ? `?${q}` : "";
}
